import React, { useState } from "react";
import "./contact-us.css";

type InquiryForm = {
  fullName: string;
  parentName: string;
  phone: string;
  email: string;
  message: string;
};

// Formspark form ID can be overridden at build time.
const runtimeEnv =
  (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env || {};
const FORMSPARK_FORM_ID = runtimeEnv.FORMSPARK_FORM_ID || "nvuNOo9fE";
const FORMSPARK_API_URL = runtimeEnv.FORMSPARK_API_URL || `https://submit-form.com/${FORMSPARK_FORM_ID}`;

const ContactUs: React.FC = () => {
  const mapEmbedUrl =
    "https://www.google.com/maps?q=S-CUBUS,+46,+Rd+Number+203,+Parmanand+Colony,+Pocket+8,+Block+B,+Sector-12B,+Dwarka,+New+Delhi,+Delhi,+110078&output=embed";
  const fullMapUrl =
    "https://www.google.com/maps/place/S-cubus+In+Dwarka,+Delhi/@28.5945614,77.039648,17z/data=!4m6!3m5!1s0x390d1b228be52fa1:0x7cff3d4cacf387d5!8m2!3d28.5945567!4d77.0422283!16s%2Fg%2F11txy1f7gs?entry=ttu&g_ep=EgoyMDI2MDIwNC4wIKXMDSoKLDEwMDc5MjA3M0gBUAM%3D";

  const [form, setForm] = useState<InquiryForm>({
    fullName: "",
    parentName: "",
    phone: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<{ type: "success" | "error" | null; message: string }>({
    type: null,
    message: "",
  });

  const onChange =
    (key: keyof InquiryForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((p) => ({ ...p, [key]: e.target.value }));
    };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitState({ type: null, message: "" });

    try {
      const response = await fetch(FORMSPARK_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const errorText = (await response.text()).trim();

        if (response.status === 404) {
          throw new Error("Inquiry form is not configured correctly. Please contact support.");
        }

        throw new Error(errorText || "Unable to send inquiry right now. Please try again.");
      }

      setSubmitState({ type: "success", message: "Inquiry submitted successfully. We'll contact you within 24 hours." });
      setForm({ fullName: "", parentName: "", phone: "", email: "", message: "" });
    } catch (error) {
      setSubmitState({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to submit inquiry. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cu-page">
      <div className="cu-container">
        <section className="cu-hero">
          <h1 className="cu-hero__title">Get in Touch</h1>
          <p className="cu-hero__subtitle">
            We are here to guide your path to success in IIT-JEE, NEET, and Olympiads.
            <br />
            Reach out to our expert counselors or visit our centers.
          </p>
        </section>

        <section className="cu-quick">
          <a className="cu-quickCard" href="tel:+9101146696001" aria-label="Call support at 01146696001">
            <div className="cu-quickIcon">
              <span className="material-symbols-outlined">call</span>
            </div>
            <div className="cu-quickText">
              <div className="cu-quickSmall">Talk to us</div>
              <div className="cu-quickBold">Call Support: 01146696001</div>
            </div>
          </a>

          <a
            className="cu-quickCard"
            href="mailto:Info@scubus.com"
            aria-label="Email us at Info@scubus.com"
          >
            <div className="cu-quickIcon">
              <span className="material-symbols-outlined">mail</span>
            </div>
            <div className="cu-quickText">
              <div className="cu-quickSmall">Send an email</div>
              <div className="cu-quickBold">Email Us: Info@scubus.com</div>
            </div>
          </a>

          <a
            className="cu-quickCard"
            href="https://wa.me/918796101095"
            target="_blank"
            rel="noreferrer"
            aria-label="Chat on WhatsApp at 8796101095"
          >
            <div className="cu-quickIcon cu-quickIcon--green">
              <span className="material-symbols-outlined">chat</span>
            </div>
            <div className="cu-quickText">
              <div className="cu-quickSmall">Chat now</div>
              <div className="cu-quickBold cu-quickBold--green">WhatsApp Inquiry: 8796101095</div>
            </div>
          </a>
        </section>

        <section className="cu-socialLinks" aria-label="Social media links">
          <a
            className="cu-socialLink"
            href="https://www.facebook.com/share/1FzuPEQd6q/?mibextid=wwXIfr"
            target="_blank"
            rel="noreferrer"
          >
            Facebook
          </a>
          <a
            className="cu-socialLink"
            href="https://www.instagram.com/s_cubus_dwarka?igsh=d2c5OXliNjVxcHF6&utm_source=qr"
            target="_blank"
            rel="noreferrer"
          >
            Instagram
          </a>
          <a
            className="cu-socialLink"
            href="https://youtube.com/@s-cubus_dwarka_delhi?si=hnNRlZpAiMBTmhbc"
            target="_blank"
            rel="noreferrer"
          >
            YouTube
          </a>
        </section>

        <section className="cu-main">
          <div className="cu-formCard">
            <div className="cu-formHead">
              <h2 className="cu-formTitle">Send an Inquiry</h2>
              <p className="cu-formSub">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>
            </div>

            <form className="cu-form" onSubmit={onSubmit}>
              <label className="cu-field">
                <span className="cu-label">Full Name</span>
                <input
                  className="cu-input"
                  type="text"
                  placeholder="Enter student name"
                  value={form.fullName}
                  onChange={onChange("fullName")}
                  required
                />
              </label>

              <label className="cu-field">
                <span className="cu-label">Parent Name</span>
                <input
                  className="cu-input"
                  type="text"
                  placeholder="Enter parent name"
                  value={form.parentName}
                  onChange={onChange("parentName")}
                />
              </label>

              <div className="cu-row2">
                <label className="cu-field">
                  <span className="cu-label">Phone Number</span>
                  <input
                    className="cu-input"
                    type="tel"
                    placeholder="+91"
                    value={form.phone}
                    onChange={onChange("phone")}
                    required
                  />
                </label>

                <label className="cu-field">
                  <span className="cu-label">Email Address</span>
                  <input
                    className="cu-input"
                    type="email"
                    placeholder="example@email.com"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                  />
                </label>
              </div>

              <label className="cu-field">
                <span className="cu-label">How can we help?</span>
                <textarea
                  className="cu-textarea"
                  rows={4}
                  placeholder="Tell us about the course you are interested in..."
                  value={form.message}
                  onChange={onChange("message")}
                  required
                />
              </label>

              <button className="cu-btnPrimary" type="submit" disabled={isSubmitting}>
                <span>{isSubmitting ? "Sending..." : "Send Inquiry"}</span>
                <span className="material-symbols-outlined cu-sendIcon">send</span>
              </button>
              <p className="cu-formFallback">(If the above feature does not work, try the form below.)</p>
              <a
                className="cu-btnPrimary cu-btnPrimary--link"
                href="https://docs.google.com/forms/d/e/1FAIpQLSfo-TqRC1SsZ40sAA7o1fqMZK-Cp0Sz4de88vlvIO_jnUp9xA/viewform"
                target="_blank"
                rel="noopener noreferrer"
              >
                Form
              </a>
              {submitState.type ? (
                <p className={`cu-submitMsg cu-submitMsg--${submitState.type}`} role="status">
                  {submitState.message}
                </p>
              ) : null}
            </form>
          </div>

          <div className="cu-right">
            <div className="cu-map">
              <iframe
                title="S-CUBUS Map"
                src={mapEmbedUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <button
                className="cu-mapBtn"
                type="button"
                onClick={() =>
                  window.open(
                    fullMapUrl,
                    "_blank"
                  )
                }
              >
                View Full Map
              </button>
            </div>

            <div className="cu-centers">
              <div className="cu-centersTitle">
                <span className="material-symbols-outlined">apartment</span>
                <h3>Our Centers</h3>
              </div>

              <div className="cu-centerCard cu-centerCard--primary">
                <div className="cu-centerTop">
                  <div>
                    <div className="cu-centerName">Main Campus - Dwarka</div>
                    <div className="cu-tag cu-tag--primary">Headquarter</div>
                  </div>
                </div>

                <div className="cu-centerInfo">
                  <div className="cu-infoRow">
                    <span className="material-symbols-outlined">location_on</span>
                  <span>
                    Floor No.: Third Floor<br />
                    Building No./Flat No.: Plot No. 46, Block No. B<br />
                    Road/Street: Sector 12<br />
                    Locality/Sub Locality: Sector 12 Dwarka<br />
                    City/Town/Village: New Delhi<br />
                    District: South East Delhi<br />
                    State: Delhi<br />
                    PIN Code: 110078
                  </span>
                  </div>
                  <div className="cu-infoRow">
                    <span className="material-symbols-outlined">schedule</span>
                    <span>Monday-Friday: 9:00 AM - 8:00 PM</span>
                    <span>Saturday-Sunday: 8:00 AM - 5:00 PM</span>
                  </div>
                  <div className="cu-infoRow">
                    <span className="material-symbols-outlined">call</span>
                    <span>+91 84483 37001</span>
                  </div>
                </div>
              </div>

              
            </div>
          </div>
        </section>
      </div>

      <section className="cu-cta">
        <div className="cu-ctaGlow cu-ctaGlow--right" />
        <div className="cu-ctaGlow cu-ctaGlow--left" />

        <div className="cu-ctaInner">
          <h2>Visit Us for a Counseling Session</h2>
          <p>
            Not sure which course is right for you? Our academic experts are here to help you chart your path to top
            colleges. Walk into any of our centers for a free personalized roadmap.
          </p>

          <div className="cu-ctaActions">
            <a
              className="cu-ctaPrimary"
              href="https://forms.gle/xdExtHf2Yss4ZaHq9"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book a Session
            </a>
            <a
              className="cu-ctaGhost"
              href="https://drive.google.com/file/d/1l5rloH-eS_x0pKcriMMPvX-Mg5DIixDU/view"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Brochure
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
